/**
 * usePhotoUploadQueue - background uploading for field photos.
 *
 * The problem this solves: a technician on a bad connection used to tap
 * "save", watch a spinner, and wait. Ten photos meant ten sequential waits,
 * each one blocking the UI, and any one of them failing meant starting over.
 *
 * With this hook the upload starts the instant the photo exists and runs in
 * the background. The caller gets an immediate optimistic entry it can render
 * (preview thumbnail + status chip) while the bytes go up behind the scenes,
 * so the tech can keep working through the checklist. Up to
 * MAX_CONCURRENT_UPLOADS run at once — enough to use the available bandwidth,
 * few enough that a weak connection isn't shredded between them.
 *
 * Failures don't lose anything: the upload closure is held for the session,
 * the entry flips to 'failed' (or 'waiting' when the device is simply
 * offline), and it retries automatically the moment connectivity returns. The
 * user can also retry by hand from the status chip.
 *
 * The hook is deliberately generic about WHAT it uploads. Callers pass an
 * `upload` closure, so the same queue serves work-order photos, inspection
 * photos, and per-checklist-item captures without knowing anything about
 * their endpoints or metadata. `meta` rides along untouched so a caller can
 * map an entry back to the checklist slot it belongs to.
 *
 * @example
 * const queue = usePhotoUploadQueue<{ photoType: 'before' | 'after' }>();
 *
 * queue.enqueue({
 *     previewUrl: photo.dataUrl,
 *     meta: { photoType: 'before' },
 *     upload: () => uploadService.uploadWorkOrderPhoto(id, photo.file, metadata),
 *     onUploaded: () => refetch(),
 * });
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/* ============================================
 * CONSTANTS
 * ============================================ */

/** How many uploads may be in flight at once. Three keeps a decent pipe
 *  busy without starving each request on a one-bar connection. */
const MAX_CONCURRENT_UPLOADS = 3;

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Lifecycle of a single background upload.
 *
 *   uploading — request in flight (or queued behind the concurrency cap)
 *   uploaded  — server accepted it; the caller's onUploaded has run
 *   waiting   — device is offline; will retry itself on reconnect
 *   failed    — the request errored; retryable by hand or on reconnect
 */
export type PhotoUploadStatus = 'uploading' | 'uploaded' | 'waiting' | 'failed';

/** One tracked upload, as rendered by the caller. */
export interface PendingPhotoUpload<TMeta = undefined> {
    /** Stable client-side id for this entry (also the React key). */
    id: string;
    /** Local preview (data URL) shown while the real photo isn't back yet. */
    previewUrl: string | null;
    status: PhotoUploadStatus;
    /** Human-readable failure reason; null unless status is 'failed'. */
    error: string | null;
    /** Caller-supplied payload, passed through untouched. */
    meta: TMeta;
}

/** Everything needed to start one background upload. */
export interface EnqueuePhotoInput<TMeta = undefined> {
    /** Optional explicit id — one is generated when omitted. */
    id?: string;
    /** Data URL for the optimistic thumbnail. */
    previewUrl?: string | null;
    /** Caller payload (checklist slot, photo type, …). */
    meta: TMeta;
    /** Performs the actual upload. Rejecting marks the entry failed. */
    upload: () => Promise<unknown>;
    /**
     * Runs after the upload succeeds — typically a refetch. Awaited before
     * the entry is dropped, so the optimistic tile stays on screen until
     * the real, server-backed photo is there to replace it. No flicker,
     * no moment where the photo appears to vanish.
     */
    onUploaded?: () => Promise<unknown> | void;
}

export interface PhotoUploadQueue<TMeta = undefined> {
    /** Entries currently tracked, in the order they were added. */
    items: PendingPhotoUpload<TMeta>[];
    /** Start a background upload. Returns the entry id. */
    enqueue: (input: EnqueuePhotoInput<TMeta>) => string;
    /** Retry a single failed or waiting entry. */
    retry: (id: string) => void;
    /** Stop tracking an entry (user dismissed it, or it's been replaced). */
    dismiss: (id: string) => void;
    /** Drop all tracking — e.g. when the surface unmounts. */
    clear: () => void;
    /** Count of entries not yet successfully uploaded. */
    pendingCount: number;
    /** True while at least one entry is uploading, waiting, or failed. */
    isUploading: boolean;
}

/* ============================================
 * HOOK
 * ============================================ */

export function usePhotoUploadQueue<TMeta = undefined>(): PhotoUploadQueue<TMeta> {
    const [items, setItems] = useState<PendingPhotoUpload<TMeta>[]>([]);

    /* Upload closures live in a ref, not state: they're not rendered, and
       keeping them out of state avoids re-running effects on every status
       change. They're held for the whole session so a retry — manual or on
       reconnect — still has the bytes. */
    const tasksRef = useRef<Map<string, EnqueuePhotoInput<TMeta>>>(new Map());

    /* Ids waiting for a free slot, and how many are in flight right now. */
    const queueRef = useRef<string[]>([]);
    const inFlightRef = useRef(0);

    /* Monotonic counter for generated ids — Date.now() collides when two
       photos are enqueued in the same millisecond (multi-select upload). */
    const idCounterRef = useRef(0);

    /* The authoritative entry list. State drives rendering, but the ref is
       what the queue machinery reads, because it updates synchronously.
       React defers functional state updaters until render, so anything that
       decides what to do next based on current statuses — the reconnect
       drain especially — would otherwise be working from stale data and
       silently do nothing. Every mutation goes through `commit`, so the two
       never diverge. */
    const itemsRef = useRef<PendingPhotoUpload<TMeta>[]>([]);

    /** Writes the entry list to both the ref and React state. */
    const commit = useCallback((next: PendingPhotoUpload<TMeta>[]) => {
        itemsRef.current = next;
        setItems(next);
    }, []);

    /** Patch one entry's status fields without touching the others. */
    const patch = useCallback((id: string, next: Partial<PendingPhotoUpload<TMeta>>) => {
        commit(itemsRef.current.map((item) => (item.id === id ? { ...item, ...next } : item)));
    }, [commit]);

    /**
     * Drains the queue up to the concurrency cap. Re-entrant: each finished
     * upload calls it again to pull the next id off the front.
     */
    const pump = useCallback(() => {
        while (inFlightRef.current < MAX_CONCURRENT_UPLOADS && queueRef.current.length > 0) {
            const id = queueRef.current.shift() as string;
            const task = tasksRef.current.get(id);
            /* Dismissed while it sat in the queue — nothing to do. */
            if (!task) continue;

            /* Offline: don't burn a request we know will fail. Park it as
               'waiting'; the online listener below re-queues it. */
            if (!navigator.onLine) {
                patch(id, { status: 'waiting', error: null });
                continue;
            }

            inFlightRef.current += 1;
            patch(id, { status: 'uploading', error: null });

            task.upload()
                .then(async () => {
                    /* Mark it uploaded before the refetch so the chip flips
                       immediately, then let the caller pull fresh data. The
                       entry is only dropped once that resolves. */
                    patch(id, { status: 'uploaded', error: null });
                    try {
                        await task.onUploaded?.();
                    } catch (err) {
                        /* A failed refetch is a display problem, not an
                           upload problem — the photo IS on the server. Log
                           and drop the entry rather than showing a false
                           failure the user would retry into a duplicate. */
                        console.warn('[usePhotoUploadQueue] Post-upload refresh failed:', err);
                    }
                    tasksRef.current.delete(id);
                    commit(itemsRef.current.filter((item) => item.id !== id));
                })
                .catch((err: unknown) => {
                    /* Went offline mid-request — treat as waiting, not
                       failed, so the user isn't asked to fix something
                       that will fix itself. */
                    const message = err instanceof Error ? err.message : 'Upload failed';
                    patch(
                        id,
                        navigator.onLine
                            ? { status: 'failed', error: message }
                            : { status: 'waiting', error: null },
                    );
                })
                .finally(() => {
                    inFlightRef.current -= 1;
                    pump();
                });
        }
    }, [patch, commit]);

    const enqueue = useCallback(
        (input: EnqueuePhotoInput<TMeta>): string => {
            idCounterRef.current += 1;
            const id = input.id ?? `upload-${idCounterRef.current}-${Math.random().toString(36).slice(2, 8)}`;

            tasksRef.current.set(id, input);
            commit([
                ...itemsRef.current,
                {
                    id,
                    previewUrl: input.previewUrl ?? null,
                    status: 'uploading',
                    error: null,
                    meta: input.meta,
                },
            ]);
            queueRef.current.push(id);
            pump();

            return id;
        },
        [commit, pump],
    );

    const retry = useCallback(
        (id: string) => {
            if (!tasksRef.current.has(id)) return;
            /* Guard against double-taps stacking the same id in the queue. */
            if (queueRef.current.includes(id)) return;
            patch(id, { status: 'uploading', error: null });
            queueRef.current.push(id);
            pump();
        },
        [patch, pump],
    );

    const dismiss = useCallback((id: string) => {
        tasksRef.current.delete(id);
        queueRef.current = queueRef.current.filter((queued) => queued !== id);
        commit(itemsRef.current.filter((item) => item.id !== id));
    }, [commit]);

    const clear = useCallback(() => {
        tasksRef.current.clear();
        queueRef.current = [];
        inFlightRef.current = 0;
        commit([]);
    }, [commit]);

    /* Auto-retry everything that's parked as soon as the device is back on
       a network. This is the whole point of holding the closures: the tech
       walks out of the stairwell and the backlog drains by itself. */
    useEffect(() => {
        const handleOnline = () => {
            /* Read from the ref, not state: this fires outside React's
               render cycle and needs the current statuses right now. */
            const stalled = itemsRef.current.filter(
                (item) =>
                    (item.status === 'waiting' || item.status === 'failed') &&
                    tasksRef.current.has(item.id) &&
                    !queueRef.current.includes(item.id),
            );
            if (stalled.length === 0) return;

            stalled.forEach((item) => queueRef.current.push(item.id));
            commit(
                itemsRef.current.map((item) =>
                    stalled.some((s) => s.id === item.id)
                        ? { ...item, status: 'uploading' as const, error: null }
                        : item,
                ),
            );
            pump();
        };

        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, [commit, pump]);

    const pendingCount = items.filter((item) => item.status !== 'uploaded').length;

    return {
        items,
        enqueue,
        retry,
        dismiss,
        clear,
        pendingCount,
        isUploading: pendingCount > 0,
    };
}

export default usePhotoUploadQueue;
